/* ==========================================================================
   STEEL & SADDLE, CORE
   Overlay controller (drawers/sheets), scroll reveal, header behaviour,
   and a tiny pub/sub. No dependencies.
   ========================================================================== */

window.SS = window.SS || {};

/* --------------------------------------------------------------------------
   Pub/sub
   -------------------------------------------------------------------------- */
SS.events = (() => {
  const map = {};
  return {
    on(name, fn) { (map[name] ||= []).push(fn); return () => SS.events.off(name, fn); },
    off(name, fn) { map[name] = (map[name] || []).filter((f) => f !== fn); },
    emit(name, data) { (map[name] || []).forEach((fn) => { try { fn(data); } catch (e) { console.error(e); } }); }
  };
})();

/* --------------------------------------------------------------------------
   Money formatting, reads the shop's active format from window.SSConfig
   -------------------------------------------------------------------------- */
SS.money = (cents) => {
  const fmt = (window.SSConfig && window.SSConfig.moneyFormat) || '${{amount}}';
  const value = (cents / 100).toFixed(2);
  const withCommas = (n, dec) => {
    const parts = parseFloat(n).toFixed(dec).split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    return parts.join('.');
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
    switch (token) {
      case 'amount':                 return withCommas(value, 2);
      case 'amount_no_decimals':     return withCommas(value, 0);
      case 'amount_with_comma_separator':
        return withCommas(value, 2).replace(/,/g, '§').replace(/\./g, ',').replace(/§/g, '.');
      case 'amount_no_decimals_with_comma_separator':
        return withCommas(value, 0).replace(/,/g, '.');
      default:                       return withCommas(value, 2);
    }
  });
};

/* --------------------------------------------------------------------------
   Focus trap
   -------------------------------------------------------------------------- */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';

SS.trapFocus = (container) => {
  const nodes = () => Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
  const onKey = (e) => {
    if (e.key !== 'Tab') return;
    const items = nodes();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  container.addEventListener('keydown', onKey);
  return () => container.removeEventListener('keydown', onKey);
};

/* --------------------------------------------------------------------------
   Overlay controller
   Any element with [data-overlay-open="ID"] opens #ID.
   Any element with [data-overlay-close] inside closes it.
   Works for drawers, sheets, and modals alike.
   -------------------------------------------------------------------------- */
SS.overlay = (() => {
  let active = null;
  let releaseFocus = null;
  let lastTrigger = null;
  let scrim = null;

  /* aria-modal alone is unreliable and the trap can be escaped, so a screen
     reader in browse mode used to read straight through the header, page and
     footer while a "modal" drawer was open. inert removes them from the
     accessibility tree and from tab order at the same time. */
  const INERT_ROOTS = 'body > header, body > main, body > footer, body > .shopify-section';
  const setInert = (el, on) => {
    document.querySelectorAll(INERT_ROOTS).forEach((root) => {
      if (root === el || root.contains(el)) return;
      if (on) root.setAttribute('inert', '');
      else root.removeAttribute('inert');
    });
  };

  const getScrim = () => {
    if (!scrim) {
      scrim = document.querySelector('[data-scrim]');
      if (!scrim) {
        scrim = document.createElement('div');
        scrim.className = 'scrim';
        scrim.setAttribute('data-scrim', '');
        document.body.appendChild(scrim);
      }
      scrim.addEventListener('click', () => SS.overlay.close());
    }
    return scrim;
  };

  const open = (id, trigger) => {
    const el = document.getElementById(id);
    if (!el || active === el) return;

    /* The cart drawer is re-rendered by replacing its node, which leaves
       `active` pointing at a node that is no longer in the document. Closing
       a detached element does nothing useful and leaves the scrim and body
       lock in whatever state they were in, so drop the reference instead. */
    if (active && !document.contains(active)) active = null;
    if (active) close();

    lastTrigger = trigger || document.activeElement;
    active = el;

    el.hidden = false;
    getScrim().classList.add('is-open');
    // force reflow so the transition runs from its start state
    void el.offsetWidth;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');

    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    releaseFocus = SS.trapFocus(el);
    setInert(el, true);
    const focusTarget = el.querySelector('[data-overlay-focus]') || el.querySelector(FOCUSABLE);
    if (focusTarget) requestAnimationFrame(() => focusTarget.focus());

    SS.events.emit('overlay:open', { id, el });
  };

  const close = () => {
    if (!active) return;
    const el = active;
    const id = el.id;

    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    getScrim().classList.remove('is-open');
    document.body.classList.remove('is-locked');

    document.querySelectorAll(`[data-overlay-open="${id}"]`).forEach((t) => t.setAttribute('aria-expanded', 'false'));

    if (releaseFocus) { releaseFocus(); releaseFocus = null; }
    setInert(el, false);
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();

    active = null;
    SS.events.emit('overlay:close', { id, el });

    // hide after the transition so it stays out of the a11y tree
    setTimeout(() => { if (!el.classList.contains('is-open')) el.hidden = true; }, 450);
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-overlay-open]');
    if (opener) {
      e.preventDefault();
      open(opener.getAttribute('data-overlay-open'), opener);
      return;
    }
    const closer = e.target.closest('[data-overlay-close]');
    if (closer) {
      /* "Start shopping" in the empty cart drawer is an <a href> carrying
         data-overlay-close. Blanket preventDefault swallowed the navigation,
         so the one control on the highest-intent empty state on the site did
         nothing but close the drawer. */
      if (!closer.matches('a[href]')) e.preventDefault();
      close();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && active) close();
  });

  /* Point `active` at the current node for an id. Sections that re-render
     themselves swap their root element out from under us; without this the
     controller keeps operating on the detached original, so the close button
     appears to do nothing while the replacement stays on screen. */
  const retarget = (id) => {
    if (!active || active.id !== id) return;
    const el = document.getElementById(id);
    if (!el) return;
    active = el;
    /* The keydown handler installed by trapFocus was bound to the node that
       has just been replaced, so after the first in-drawer quantity change
       there was no focus trap at all and Tab walked out into the page
       behind the scrim. */
    if (releaseFocus) releaseFocus();
    releaseFocus = SS.trapFocus(el);
    setInert(el, true);
  };

  return { open, close, retarget, get current() { return active; } };
})();

/* --------------------------------------------------------------------------
   Scroll reveal
   -------------------------------------------------------------------------- */
SS.reveal = () => {
  const items = document.querySelectorAll('[data-reveal]:not(.is-revealed)');
  if (!items.length) return;

  if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    items.forEach((el) => el.classList.add('is-revealed'));
    return;
  }

  document.documentElement.classList.add('js-reveal');

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-revealed');
      io.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

  items.forEach((el) => {
    // stagger siblings within the same group
    const group = el.closest('[data-reveal-group]');
    if (group) {
      const idx = Array.from(group.querySelectorAll('[data-reveal]')).indexOf(el);
      el.style.setProperty('--reveal-delay', `${Math.min(idx, 5) * 45}ms`);
    }
    io.observe(el);
  });

  /* Safety net.
     An observer callback that never fires leaves content at opacity 0, and a
     blank band is a far worse outcome than a missing fade. Anything that has
     reached the viewport and is still hidden a second and a half later gets
     shown regardless: a jumped scroll position, a restored position, a tab
     woken from the background, a browser that throttles the observer. */
  const sweep = () => {
    document.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-revealed');
    });
  };
  setTimeout(sweep, 1500);
  window.addEventListener('pageshow', () => setTimeout(sweep, 300));
};

/* --------------------------------------------------------------------------
   Header, shrink + hide-on-scroll-down, and transparent-over-hero
   -------------------------------------------------------------------------- */
SS.header = () => {
  const header = document.querySelector('[data-header]');
  if (!header) return;

  /* Same guard. A second scroll listener is harmless functionally but pointless,
     and a second resize listener compounds on every editor section reload. */
  if (header.dataset.headerBound === 'true') return;
  header.dataset.headerBound = 'true';

  /* --header-actual is what sticky sub-bars (the collection toolbar) offset
     themselves by, so it reports the space the header occupies. The header is
     always present now, so this is simply its height. */
  const setHeight = () => {
    document.documentElement.style.setProperty('--header-actual', `${header.offsetHeight}px`);
  };
  setHeight();
  if (!window.__ssHeaderResizeBound) {
    window.__ssHeaderResizeBound = true;
    window.addEventListener('resize', () => {
      const h = document.querySelector('[data-header]');
      if (h) document.documentElement.style.setProperty('--header-actual', `${h.offsetHeight}px`);
    }, { passive: true });
  }

  let ticking = false;

  /* The header does NOT hide on scroll.

     It used to slide away on any downward scroll past 2.5x its own height and
     return on the way back up. That is a common pattern, but it is not what
     this store wants: Blake has now twice reported the header as "not sticky",
     and both times the cause was this, not the sticky positioning. A bar that
     vanishes the moment you scroll reads as broken rather than considered,
     and it took the search, account and cart controls with it.

     All that remains on scroll is the stuck state, which only adds a hairline
     shadow once you have left the top of the page. */
  /* Where the bar actually sticks: the height of whatever sits above it
     (the announcement strip), measured once and again on resize. v48 flipped
     the state at 8px of scroll, which was fine for a bar that only gained a
     hairline shadow. Now the stuck state also turns a floating bar solid, so
     it has to flip at the exact pixel the bar meets the top of the screen,
     or it goes solid while still hanging over the photograph. */
  const shell = header.closest('.hdr-shell') || header;
  let stuckAt = 0;
  const measureStuck = () => {
    /* The bar sticks where the section above it ends. Read from that sibling
       rather than from the shell itself: a sticky element's own offsetTop
       moves with it once it has stuck. */
    const above = shell.previousElementSibling;
    stuckAt = above ? Math.round(above.offsetHeight) : 0;
  };
  measureStuck();

  const onScroll = () => {
    /* setHeight() used to run here too. It reads offsetHeight, which forces
       layout, then writes a custom property on documentElement, which
       invalidates style for the whole document. Once per animation frame for
       the length of every scroll, to recompute a number that only changes on
       resize. */
    const y = window.scrollY;
    header.classList.toggle('is-stuck', y > 0 && y >= stuckAt);
    ticking = false;
  };
  if (!window.__ssHeaderStuckResizeBound) {
    window.__ssHeaderStuckResizeBound = true;
    window.addEventListener('resize', () => { measureStuck(); onScroll(); }, { passive: true });
    /* Back/forward restores the scroll position before any scroll event
       fires, so a page restored mid-scroll would keep a floating bar over
       solid content until the first nudge. */
    window.addEventListener('pageshow', () => { measureStuck(); onScroll(); });
  }

  /* Bound to window, so the guard has to live on window too. The old guard
     was stored on the header element, which is replaced on every
     shopify:section:load, leaving the previous scroll and resize listeners
     attached and holding a reference to a detached node. */
  if (!window.__ssHeaderScrollBound) {
    window.__ssHeaderScrollBound = true;
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(onScroll); }
    }, { passive: true });
  }

  onScroll();
};

/* --------------------------------------------------------------------------
   Desktop nav dropdowns
   -------------------------------------------------------------------------- */
SS.nav = () => {
  document.querySelectorAll('[data-nav-item]').forEach((item) => {
    const trigger = item.querySelector('[data-nav-trigger]');
    const menu = item.querySelector('[data-nav-menu]');
    if (!trigger || !menu) return;

    /* Bind once. This function re-runs on every shopify:section:load, and
       without this guard each run stacked another click listener, so the
       toggle fired twice, opening then instantly closing, and the dropdown
       appeared not to work at all. */
    if (item.dataset.navBound === 'true') return;
    item.dataset.navBound = 'true';

    let timer;
    const open = () => { clearTimeout(timer); item.classList.add('is-open'); trigger.setAttribute('aria-expanded', 'true'); };
    const close = () => { timer = setTimeout(() => { item.classList.remove('is-open'); trigger.setAttribute('aria-expanded', 'false'); }, 120); };

    /* The trigger managed aria-expanded but pointed at nothing, and Escape
       did not close the panel. Both are part of the disclosure contract. */
    if (!menu.id) menu.id = `nav-menu-${Math.random().toString(36).slice(2, 8)}`;
    trigger.setAttribute('aria-controls', menu.id);
    trigger.setAttribute('aria-haspopup', 'true');
    if (!trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');

    item.addEventListener('mouseenter', open);
    item.addEventListener('mouseleave', close);
    item.addEventListener('focusin', open);
    item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) close(); });
    item.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !item.classList.contains('is-open')) return;
      e.stopPropagation();
      clearTimeout(timer);
      item.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus();
    });
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      item.classList.contains('is-open') ? close() : open();
    });
  });

  // Mobile nav: expandable sub-menus
  document.querySelectorAll('[data-subnav-toggle]').forEach((btn) => {
    if (btn.dataset.subnavBound === 'true') return;
    btn.dataset.subnavBound = 'true';
    btn.addEventListener('click', () => {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      const panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (panel) panel.hidden = expanded;
    });
  });
};

/* --------------------------------------------------------------------------
   PREDICTIVE SEARCH

   The theme carried a `predictive_search` setting, switched on by default,
   with no implementation behind it: the panel posted a plain GET form and
   suggested nothing. This uses Shopify's own predictive endpoint, already
   exposed on window.SSConfig.routes, so there is no app and no third-party
   script. The form still submits normally with JavaScript off.
   -------------------------------------------------------------------------- */
SS.predictiveSearch = () => {
  const input = document.querySelector('[data-predictive]');
  const zone = document.querySelector('[data-predictive-results]');
  const status = document.querySelector('[data-predictive-status]');
  if (!input || !zone || input.dataset.predictiveBound === 'true') return;
  input.dataset.predictiveBound = 'true';

  const routes = (window.SSConfig && window.SSConfig.routes) || {};
  const endpoint = routes.predictive_search_url || '/search/suggest';
  const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));

  /* /search/suggest.json sends price as a decimal string in dollars
     ("54.00"), while SS.money takes integer cents like the cart does, so a
     $54 tee read $0.54. Convert here and leave SS.money alone: a number is
     already cents, a string is dollars. */
  const toCents = (v) => {
    if (typeof v === 'number') return Number.isFinite(v) ? Math.round(v) : null;
    const n = parseFloat(v);
    return Number.isFinite(n) ? Math.round(n * 100) : null;
  };
  const priceOf = (p) => {
    const cents = toCents(p.price);
    return cents == null ? '' : SS.money(cents);
  };

  /* v76: carried brands sold as Steel & Saddle Outfitters carry the tag
     flagship-online, the same rule as snippets/is-outfitters. They can turn
     up in search, so the brand prints under the title and they never read
     as our own line. */
  const vendorOf = (p) => {
    const tags = Array.isArray(p.tags) ? p.tags : [];
    if (!tags.includes('flagship-online') || !p.vendor) return '';
    return `<span class="searchpanel__hit-vendor">${esc(p.vendor)}</span>`;
  };

  let timer;
  let controller;

  const clear = () => {
    zone.hidden = true;
    zone.innerHTML = '';
    input.setAttribute('aria-expanded', 'false');
  };

  const paint = (products, term) => {
    if (!products.length) {
      zone.innerHTML = `<p class="searchpanel__empty">We don't make "${esc(term)}". Tees, hoodies and caps. That's the whole line.</p>`;
      if (status) status.textContent = 'No suggestions';
    } else {
      /* v86: product names show with "The" in front, same as snippets/product-name. */
      const theName = (t) => (/^the\s/i.test(String(t || '')) ? t : `The ${t}`);
      zone.innerHTML = products.map((p) => `
        <a class="searchpanel__hit" role="option" href="${esc(p.url)}">
          ${p.featured_image && p.featured_image.url
            ? `<img src="${esc(p.featured_image.url)}" alt="" loading="lazy" width="48" height="60">`
            : ''}
          <span class="searchpanel__hit-title">${esc(theName(p.title))}${vendorOf(p)}</span>
          <span class="searchpanel__hit-price">${priceOf(p)}</span>
        </a>`).join('');
      if (status) {
        status.textContent = `${products.length} ${products.length === 1 ? 'suggestion' : 'suggestions'}`;
      }
    }
    zone.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  };

  const run = async (term) => {
    if (controller) controller.abort();
    controller = new AbortController();
    const url = `${endpoint}?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=5&section_id=`;
    try {
      const res = await fetch(url.replace(/&section_id=$/, '') + '&resources[options][unavailable_products]=last', {
        headers: { Accept: 'application/json' },
        signal: controller.signal
      });
      if (!res.ok) return clear();
      const data = await res.json();
      paint((data.resources && data.resources.results && data.resources.results.products) || [], term);
    } catch (err) {
      if (err.name !== 'AbortError') clear();
    }
  };

  input.addEventListener('input', () => {
    const term = input.value.trim();
    clearTimeout(timer);
    if (term.length < 2) return clear();
    timer = setTimeout(() => run(term), 220);
  });

  /* Arrow keys walk the suggestions, Escape returns to the field rather than
     closing the whole panel out from under the shopper. */
  input.addEventListener('keydown', (e) => {
    if (zone.hidden) return;
    const hits = Array.from(zone.querySelectorAll('.searchpanel__hit'));
    if (!hits.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); hits[0].focus(); }
    if (e.key === 'Escape') { e.stopPropagation(); clear(); }
  });

  zone.addEventListener('keydown', (e) => {
    const hits = Array.from(zone.querySelectorAll('.searchpanel__hit'));
    const i = hits.indexOf(document.activeElement);
    if (i === -1) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); (hits[i + 1] || input).focus(); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); (hits[i - 1] || input).focus(); }
    if (e.key === 'Escape')    { e.stopPropagation(); clear(); input.focus(); }
  });
};



/* --------------------------------------------------------------------------
   ANNOUNCEMENT ROTATION

   Deliberately dumb: swap a class, no transform, no transition. Rotating
   banners test badly for readability, so the only thing that makes this
   acceptable is that each message sits still long enough to be read and that
   it stops the moment someone interacts with it.

   Fully disabled under prefers-reduced-motion, where the first message simply
   stays put.
   -------------------------------------------------------------------------- */
SS.announcement = () => {
  document.querySelectorAll('[data-announce]').forEach((bar) => {
    if (bar.dataset.announceBound === 'true') return;
    bar.dataset.announceBound = 'true';

    const slides = Array.from(bar.querySelectorAll('[data-announce-slide]'));
    if (slides.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const wait = parseInt(bar.getAttribute('data-interval'), 10) || 6000;
    let i = 0;
    let timer;

    const show = (next) => {
      slides[i].classList.remove('is-active');
      slides[i].setAttribute('aria-hidden', 'true');
      i = next;
      slides[i].classList.add('is-active');
      slides[i].removeAttribute('aria-hidden');
    };

    const stop = () => clearInterval(timer);
    /* start clears first: mouseleave and focusout can both fire after one
       stop, and two intervals would make the bar flicker. */
    const start = () => { stop(); timer = setInterval(() => show((i + 1) % slides.length), wait); };

    /* Pause while a shopper is reading or tabbing through it, and while the
       tab is in the background. */
    bar.addEventListener('mouseenter', stop);
    bar.addEventListener('mouseleave', start);
    bar.addEventListener('focusin', stop);
    bar.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));

    start();
  });
};

/* --------------------------------------------------------------------------
   INSTANT NAVIGATION

   Two independent mechanisms, both progressive enhancements:

   1. Prefetch on intent. Hovering a link for 65ms, or touching it, is a
      strong signal the shopper is going there. Fetching the document then
      means the click lands on a warm cache. This is the single cheapest
      thing that makes a site feel expensive: no white flash between pages.

   2. Cross-document view transitions, where the browser supports them. The
      opt-in lives in the <head> as a meta tag; this file only handles the
      prefetching and the safety rails.

   Rails, because an over-eager prefetcher is a bandwidth tax on someone
   else's phone plan:
     - same origin only, no downloads, no cart or account URLs
     - never on a metered or slow connection, never under Save-Data
     - a hard cap per page view
     - <link rel="prefetch">, which is low priority by definition and which
       the browser is free to ignore
   -------------------------------------------------------------------------- */
SS.instantNav = () => {
  if (window.__ssPrefetchBound) return;
  window.__ssPrefetchBound = true;

  const conn = navigator.connection || {};
  if (conn.saveData) return;
  if (/2g/.test(conn.effectiveType || '')) return;
  if (window.matchMedia('(prefers-reduced-data: reduce)').matches) return;

  const MAX = 8;
  const done = new Set();
  let budget = MAX;

  /* Anything that changes state, or that is not a document, is off limits.
     Prefetching /cart/add or an account URL would be actively harmful. */
  const SKIP = /\/cart|\/account|\/checkout|\/challenge|\/apps\/|\.(jpg|jpeg|png|webp|avif|pdf|zip|csv)$/i;

  const eligible = (a) => {
    if (!a || !a.href) return false;
    if (a.target && a.target !== '_self') return false;
    if (a.hasAttribute('download')) return false;
    if (a.dataset.noPrefetch !== undefined) return false;
    let u;
    try { u = new URL(a.href); } catch { return false; }
    if (u.origin !== location.origin) return false;
    if (u.pathname === location.pathname) return false;
    if (SKIP.test(u.pathname)) return false;
    return !done.has(u.pathname + u.search);
  };

  const prefetch = (a) => {
    if (budget <= 0 || !eligible(a)) return;
    const u = new URL(a.href);
    done.add(u.pathname + u.search);
    budget -= 1;
    const l = document.createElement('link');
    l.rel = 'prefetch';
    l.as = 'document';
    l.href = a.href;
    document.head.appendChild(l);
  };

  let timer;
  document.addEventListener('mouseover', (e) => {
    const a = e.target.closest && e.target.closest('a[href]');
    if (!a) return;
    clearTimeout(timer);
    timer = setTimeout(() => prefetch(a), 65);
  }, { passive: true });
  document.addEventListener('mouseout', () => clearTimeout(timer), { passive: true });

  /* On touch there is no hover, but there are ~90ms between finger down and
     the click firing, which is enough to start the document. */
  document.addEventListener('touchstart', (e) => {
    const a = e.target.closest && e.target.closest('a[href]');
    if (a) prefetch(a);
  }, { passive: true, capture: true });
};

/* --------------------------------------------------------------------------
   Hero video
   Fades the loop in once it can actually play, and pauses it while the hero
   is off screen so a phone is not decoding video it cannot see. The still
   underneath is the LCP element either way; nothing here delays it.
   -------------------------------------------------------------------------- */
SS.heroVideo = () => {
  document.querySelectorAll('[data-hero-video]').forEach((wrap) => {
    if (wrap.dataset.bound === 'true') return;
    wrap.dataset.bound = 'true';
    const v = wrap.querySelector('video');
    if (!v) return;
    v.muted = true; v.loop = true; v.playsInline = true;
    const show = () => wrap.classList.add('is-playing');
    v.addEventListener('playing', show, { once: true });
    if (!v.paused && v.readyState >= 3) show();
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
          else v.pause();
        });
      }, { threshold: 0.05 }).observe(wrap);
    } else {
      const p = v.play(); if (p && p.catch) p.catch(() => {});
    }
  });
};

/* --------------------------------------------------------------------------
   Boot
   Kept at the very bottom of the file so every SS.* function it calls is
   already defined. Moving a new function below this block silently stops it
   from ever initialising on a page that has finished parsing.
   -------------------------------------------------------------------------- */
const boot = () => {
  SS.header();
  SS.nav();
  SS.reveal();
  SS.predictiveSearch();
  SS.announcement();
  SS.instantNav();
  SS.heroVideo();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

// Re-run on Shopify theme-editor section reloads
document.addEventListener('shopify:section:load', boot);
